/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  RequestStationActivityInterface,
  StationActivityResponseInterface,
  StationActivityInitialProperties,
} from '../../interface/station-activity-interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  ReplaceValueInFilter,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import stationManagementApi from '../../utils/api/station-management';

// a thunk to fetch users
export const fetchStationActivity = createAsyncThunk<
  StationActivityResponseInterface,
  RequestStationActivityInterface
>(
  'station-activity/fetchStationActivity',
  async (data: RequestStationActivityInterface, { dispatch }) => {
    try {
      const response = await stationManagementApi.fetchStationActivityList(removeEmptyKeys(data));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchStationActivityFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  { chargingStation: string | undefined }
>('station-activity/fetchStationActivityFilters', async ({ chargingStation }, { dispatch }) => {
  if (!chargingStation) {
    throw new Error('chargingStation is required');
  }
  try {
    const response = await stationManagementApi.fetchStationActivityListFilters({
      chargingStation,
    });
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: StationActivityInitialProperties = {
  stationsActivity: [],
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
  LastUpdatedFiled:{
    lastUpdatedTime: '',
    lastMins:false
  }
};

export const stationActivitySlice = createSlice({
  name: 'station-activity',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param state
     * @param action
     */
    updateStationActivityFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      } else if (payload.type === 'replace') {
        const { updatedCommonFilter, isFilterUpdated } = ReplaceValueInFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disableStationActivityFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Function for sorting data by it's field
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
    /**
     * setLastUpdatedField
     */
    setLastUpdatedField: (state, action: PayloadAction<{lastUpdatedTime:string; lastMins:boolean}>) => {
      state.LastUpdatedFiled = action.payload;
    }
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<StationActivityInitialProperties>) => {
    builder
      .addCase(fetchStationActivity.pending, (state: StationActivityInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchStationActivity.fulfilled,
        (
          state: StationActivityInitialProperties,
          action: PayloadAction<StationActivityResponseInterface>,
        ) => {
          state.loading = false;
          state.stationsActivity = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchStationActivity.rejected, (state: StationActivityInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchStationActivityFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchStationActivityFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...availableFilters, ...action.payload];
        },
      )
      .addCase(fetchStationActivityFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { updateStationActivityFilters, disableStationActivityFiltersApplied, setSortData,setLastUpdatedField } =
  stationActivitySlice.actions;
// Export the reducer
export default stationActivitySlice.reducer;
